import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ShoppingBag,
  X,
  Loader2,
  Check,
  AlertTriangle,
  Package,
  Image as ImageIcon,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Tag,
  Edit3,
  ToggleLeft,
  ToggleRight,
  Search,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Eye,
  EyeOff,
  Link2,
  FileText,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '../../config/firebase';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

const PRODUCT_CATEGORIES = [
  'Dresses', 'Tops', 'Bottoms', 'Outerwear', 'Swimwear',
  'Activewear', 'Accessories', 'Shoes', 'Jewelry', 'Bags',
  'Jumpsuits', 'Lingerie', 'Sets', 'Other',
];

interface ShopifyVariant {
  id: number;
  title: string;
  price: string;
  compare_at_price: string | null;
  sku: string;
  inventory_quantity: number;
  option1: string | null;
}

interface ShopifyProduct {
  id: number;
  title: string;
  product_type: string;
  status: string;
  images: Array<{ src: string }>;
  variants: ShopifyVariant[];
  tags: string;
}

interface EditableProduct {
  shopifyId: number;
  included: boolean;
  name: string;
  price: string;
  compareAtPrice: string;
  category: string;
  tags: string;
  image: string;
  originalTitle: string;
  variants: Array<{
    label: string;
    stock: number;
    included: boolean;
    originalLabel: string;
  }>;
}

type FlowStep = 'connect' | 'loading' | 'select' | 'review' | 'importing' | 'done';

interface ShopifyImportProps {
  businessId: string;
  onClose: () => void;
  onImportComplete?: () => void;
}

function mapCategory(productType: string): string {
  const t = (productType || '').toLowerCase();
  const map: Record<string, string> = {
    dress: 'Dresses', dresses: 'Dresses',
    top: 'Tops', tops: 'Tops', shirt: 'Tops', blouse: 'Tops',
    bottom: 'Bottoms', bottoms: 'Bottoms', pants: 'Bottoms', jeans: 'Bottoms', skirt: 'Bottoms', shorts: 'Bottoms',
    outerwear: 'Outerwear', jacket: 'Outerwear', coat: 'Outerwear',
    swimwear: 'Swimwear', swim: 'Swimwear', bikini: 'Swimwear',
    activewear: 'Activewear', athletic: 'Activewear',
    accessories: 'Accessories', accessory: 'Accessories', hat: 'Accessories', scarf: 'Accessories',
    shoes: 'Shoes', shoe: 'Shoes', boots: 'Shoes', sneakers: 'Shoes', sandals: 'Shoes',
    jewelry: 'Jewelry', necklace: 'Jewelry', bracelet: 'Jewelry', earrings: 'Jewelry',
    bag: 'Bags', bags: 'Bags', purse: 'Bags', handbag: 'Bags',
    jumpsuit: 'Jumpsuits', romper: 'Jumpsuits',
    lingerie: 'Lingerie',
    set: 'Sets', sets: 'Sets',
  };
  for (const [kw, cat] of Object.entries(map)) {
    if (t.includes(kw)) return cat;
  }
  return 'Other';
}

const STEP_LABELS: Record<FlowStep, { num: number; label: string }> = {
  connect: { num: 1, label: 'Connect' },
  loading: { num: 2, label: 'Loading' },
  select: { num: 2, label: 'Select' },
  review: { num: 3, label: 'Review' },
  importing: { num: 3, label: 'Import' },
  done: { num: 3, label: 'Done' },
};

function StepIndicator({ currentStep }: { currentStep: FlowStep }) {
  const steps = [
    { num: 1, label: 'Connect' },
    { num: 2, label: 'Select' },
    { num: 3, label: 'Import' },
  ];
  const current = STEP_LABELS[currentStep].num;

  return (
    <div className="flex items-center gap-1 px-5 py-3 border-b border-gray-100">
      {steps.map((s, i) => (
        <React.Fragment key={s.num}>
          <div className="flex items-center gap-1.5">
            <div
              className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors',
                s.num < current
                  ? 'bg-green-500 text-white'
                  : s.num === current
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-400'
              )}
            >
              {s.num < current ? <Check className="h-3.5 w-3.5" /> : s.num}
            </div>
            <span
              className={cn(
                'text-xs font-medium transition-colors',
                s.num <= current ? 'text-heading' : 'text-gray-400'
              )}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={cn('flex-1 h-px mx-2', s.num < current ? 'bg-green-300' : 'bg-gray-200')} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export function ShopifyImport({ businessId, onClose, onImportComplete }: ShopifyImportProps) {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [storeName, setStoreName] = useState('');
  const [step, setStep] = useState<FlowStep>('connect');

  // Connect fields
  const [shopDomain, setShopDomain] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  // Product state
  const [editableProducts, setEditableProducts] = useState<EditableProduct[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    errors: string[];
  } | null>(null);

  // Prevents re-entering "loading products" when the business doc updates
  // mid-flow (e.g. lastSyncAt after import). The old effect closed over
  // `step === 'connect'` forever and kept calling loadProducts().
  const hasLoadedProductsRef = useRef(false);

  const loadProducts = useCallback(async () => {
    setStep('loading');
    setError(null);
    try {
      const getProducts = httpsCallable(functions, 'getShopifyProducts');
      const result = await getProducts({});
      const data = result.data as { products: ShopifyProduct[] };
      const products = data.products || [];
      const editable: EditableProduct[] = products.map((p) => {
        const mainVariant = p.variants[0];
        return {
          shopifyId: p.id,
          included: true,
          name: p.title,
          price: mainVariant?.price || '0',
          compareAtPrice: mainVariant?.compare_at_price || '',
          category: mapCategory(p.product_type),
          tags: p.tags || '',
          image: p.images[0]?.src || '',
          originalTitle: p.title,
          variants: p.variants.map((v) => ({
            label: v.option1 || v.title || 'One Size',
            stock: Math.max(0, v.inventory_quantity || 0),
            included: true,
            originalLabel: v.option1 || v.title || 'One Size',
          })),
        };
      });
      setEditableProducts(editable);
      setStep('select');
    } catch (err: any) {
      setError(err?.message || 'Failed to load products');
      setStep('select');
      // Allow a manual retry after a failed load.
      hasLoadedProductsRef.current = false;
    }
  }, []);

  // Check connection status on mount — only auto-load products once.
  useEffect(() => {
    hasLoadedProductsRef.current = false;
    const unsub = onSnapshot(doc(db, 'businesses', businessId), (snap) => {
      const data = snap.data();
      const connected = data?.shopify?.connected === true;
      setIsConnected(connected);
      setStoreName(data?.shopify?.storeName || '');
      if (connected && !hasLoadedProductsRef.current) {
        hasLoadedProductsRef.current = true;
        loadProducts();
      }
    });
    return () => unsub();
  }, [businessId, loadProducts]);

  const handleConnect = async () => {
    const domain = shopDomain.trim();
    const token = accessToken.trim();
    if (!domain || !token) {
      setConnectError('Both fields are required');
      return;
    }
    setConnecting(true);
    setConnectError(null);
    try {
      const connectFn = httpsCallable(functions, 'connectShopify');
      const result = await connectFn({ shopDomain: domain, accessToken: token });
      const data = result.data as { success: boolean; storeName: string };
      setStoreName(data.storeName);
      setIsConnected(true);
      // Snapshot will also fire; the ref stops a double-load.
      if (!hasLoadedProductsRef.current) {
        hasLoadedProductsRef.current = true;
        loadProducts();
      }
    } catch (err: any) {
      setConnectError(err?.message || 'Failed to connect');
    } finally {
      setConnecting(false);
    }
  };

  const includedCount = editableProducts.filter((p) => p.included).length;

  const updateProduct = useCallback((shopifyId: number, updates: Partial<EditableProduct>) => {
    setEditableProducts((prev) =>
      prev.map((p) => (p.shopifyId === shopifyId ? { ...p, ...updates } : p))
    );
  }, []);

  const updateVariant = useCallback((shopifyId: number, idx: number, updates: Partial<EditableProduct['variants'][0]>) => {
    setEditableProducts((prev) =>
      prev.map((p) => {
        if (p.shopifyId !== shopifyId) return p;
        const variants = [...p.variants];
        variants[idx] = { ...variants[idx], ...updates };
        return { ...p, variants };
      })
    );
  }, []);

  const handleImport = async () => {
    const toImport = editableProducts.filter((p) => p.included);
    if (toImport.length === 0) return;
    setStep('importing');
    setError(null);
    try {
      const importFn = httpsCallable(functions, 'importFromShopify');
      const result = await importFn({ productIds: toImport.map((p) => p.shopifyId) });
      const data = result.data as { imported: number; skipped: number; errors: string[] };
      setImportResult(data);
      setStep('done');
      if (data.imported > 0) onImportComplete?.();
    } catch (err: any) {
      setError(err?.message || 'Import failed');
      setStep('review');
    }
  };

  const filtered = editableProducts.filter((p) =>
    !searchQuery ||
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Still figuring out initial state
  if (isConnected === null) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-12 text-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // ── Done ───────────────────────────────────────────────────
  if (step === 'done' && importResult) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <StepIndicator currentStep="done" />
          <div className="p-8 text-center space-y-5">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-heading">{importResult.imported}</p>
              <p className="text-sm text-body mt-1">
                product{importResult.imported !== 1 ? 's' : ''} imported
              </p>
            </div>
            {importResult.skipped > 0 && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-xl px-4 py-2.5 inline-block">
                {importResult.skipped} already existed — skipped
              </p>
            )}
            {importResult.errors.length > 0 && (
              <div className="text-left bg-red-50 rounded-xl p-3 space-y-1">
                <p className="text-xs font-medium text-red-700">Errors:</p>
                {importResult.errors.slice(0, 3).map((e, i) => (
                  <p key={i} className="text-xs text-red-600 truncate">{e}</p>
                ))}
              </div>
            )}
            <Button onClick={onClose} className="w-full" size="lg">Done</Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Importing ──────────────────────────────────────────────
  if (step === 'importing') {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <StepIndicator currentStep="importing" />
          <div className="p-12 text-center space-y-5">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
            </div>
            <div>
              <p className="text-lg font-semibold text-heading">Importing products...</p>
              <p className="text-sm text-body mt-1">
                Bringing {includedCount} product{includedCount !== 1 ? 's' : ''} into your boutique
              </p>
            </div>
            <p className="text-xs text-body">This may take a minute.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Flow Modal ────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <ShoppingBag className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-serif text-heading">Shopify Import</h2>
              <p className="text-xs text-body">
                {step === 'connect' && 'Connect your store to get started'}
                {step === 'loading' && 'Fetching your catalog...'}
                {step === 'select' && 'Toggle products on or off'}
                {step === 'review' && 'Edit details before importing'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <X className="h-5 w-5 text-body" />
          </button>
        </div>

        <StepIndicator currentStep={step} />

        {/* ── STEP 1: Connect ──────────────────────────────── */}
        {step === 'connect' && !isConnected && (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50/80 border border-blue-100">
              <FileText className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-700 space-y-1">
                <p className="font-medium">How to get your access token:</p>
                <ol className="list-decimal list-inside space-y-0.5 text-blue-600">
                  <li><strong>Shopify Admin</strong> &rarr; Settings &rarr; Apps &rarr; <strong>Develop apps</strong></li>
                  <li>Create an app, enable scopes: <code className="bg-blue-100 px-1 rounded">read_products</code>, <code className="bg-blue-100 px-1 rounded">read_inventory</code>, <code className="bg-blue-100 px-1 rounded">write_inventory</code></li>
                  <li>Install &rarr; copy the <strong>Admin API access token</strong></li>
                </ol>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-heading">Shop Domain</label>
              <Input
                type="text"
                placeholder="myboutique.myshopify.com"
                value={shopDomain}
                onChange={(e) => { setShopDomain(e.target.value); setConnectError(null); }}
                className="font-mono text-xs"
                autoComplete="off"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-heading">Access Token</label>
              <div className="relative">
                <Input
                  type={showToken ? 'text' : 'password'}
                  placeholder="shpat_..."
                  value={accessToken}
                  onChange={(e) => { setAccessToken(e.target.value); setConnectError(null); }}
                  className="font-mono text-xs pr-10"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {connectError && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{connectError}</p>
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button
                onClick={handleConnect}
                disabled={connecting || !shopDomain.trim() || !accessToken.trim()}
                className="flex-1"
              >
                {connecting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</>
                ) : (
                  <><Link2 className="h-4 w-4 mr-2" />Connect &amp; Load Products</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── Loading Products ─────────────────────────────── */}
        {step === 'loading' && (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="text-center space-y-4">
              <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" />
              <div>
                <p className="text-sm font-medium text-heading">
                  {storeName ? `Loading from ${storeName}...` : 'Fetching your catalog...'}
                </p>
                <p className="text-xs text-body mt-1">This usually takes a few seconds</p>
              </div>
            </div>
          </div>
        )}

        {/* ── Error / Empty states ─────────────────────────── */}
        {step === 'select' && error && editableProducts.length === 0 && (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="text-center space-y-4 max-w-xs">
              <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto" />
              <p className="text-sm text-body">{error}</p>
              <Button variant="outline" onClick={loadProducts}>Try Again</Button>
            </div>
          </div>
        )}

        {step === 'select' && !error && editableProducts.length === 0 && (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="text-center space-y-3">
              <Package className="h-10 w-10 text-gray-300 mx-auto" />
              <p className="text-sm font-medium text-heading">No products found</p>
              <p className="text-xs text-body max-w-xs mx-auto">
                Your Shopify store doesn't have any active products.
              </p>
            </div>
          </div>
        )}

        {/* ── STEP 2: Select ───────────────────────────────── */}
        {step === 'select' && editableProducts.length > 0 && (
          <>
            <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 h-10 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-primary"
                />
              </div>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    const all = editableProducts.every((p) => p.included);
                    setEditableProducts((prev) => prev.map((p) => ({ ...p, included: !all })));
                  }}
                  className="text-xs font-medium text-primary hover:text-primary-hover"
                >
                  {editableProducts.every((p) => p.included) ? 'Deselect all' : 'Select all'}
                </button>
                <span className="text-xs text-body">
                  <span className="font-semibold text-heading">{includedCount}</span> of {editableProducts.length} selected
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filtered.map((product) => (
                <div
                  key={product.shopifyId}
                  className={cn(
                    'flex items-center gap-3 px-5 py-3 border-b border-gray-50 transition-colors',
                    product.included ? 'bg-white' : 'bg-gray-50/50'
                  )}
                >
                  <button
                    onClick={() => updateProduct(product.shopifyId, { included: !product.included })}
                    className="flex-shrink-0"
                  >
                    {product.included ? (
                      <ToggleRight className="h-7 w-7 text-primary" />
                    ) : (
                      <ToggleLeft className="h-7 w-7 text-gray-300" />
                    )}
                  </button>
                  <div className={cn('w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 transition-opacity', !product.included && 'opacity-40')}>
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <ImageIcon className="h-5 w-5 text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div className={cn('flex-1 min-w-0 transition-opacity', !product.included && 'opacity-40')}>
                    <p className="text-sm font-medium text-heading truncate">{product.name}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs font-medium text-heading">${parseFloat(product.price).toFixed(2)}</span>
                      <span className="text-xs text-gray-300">&middot;</span>
                      <span className="text-xs text-body">{product.variants.length} size{product.variants.length !== 1 ? 's' : ''}</span>
                      <span className="text-xs text-gray-300">&middot;</span>
                      <span className={cn('text-xs', product.variants.reduce((s, v) => s + v.stock, 0) === 0 ? 'text-red-500' : 'text-body')}>
                        {product.variants.reduce((s, v) => s + v.stock, 0)} units
                      </span>
                    </div>
                    <span className="text-[10px] text-primary bg-primary/5 px-1.5 py-0.5 rounded mt-1 inline-block">
                      {product.category}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex items-center gap-3 flex-shrink-0 bg-white">
              <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button onClick={() => setStep('review')} disabled={includedCount === 0} className="flex-1">
                Review {includedCount > 0 ? `(${includedCount})` : ''}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </>
        )}

        {/* ── STEP 3: Review & Edit ────────────────────────── */}
        {step === 'review' && (
          <>
            <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0 flex items-center justify-between">
              <p className="text-xs text-body">
                <span className="font-semibold text-heading">{includedCount}</span> product{includedCount !== 1 ? 's' : ''} ready
              </p>
              <button
                onClick={() => setExpandedId(expandedId === -1 ? null : -1)}
                className="text-xs font-medium text-primary hover:text-primary-hover"
              >
                {expandedId === -1 ? 'Collapse all' : 'Expand all'}
              </button>
            </div>

            {error && (
              <div className="mx-5 mt-3 flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {editableProducts.filter((p) => p.included).map((product) => {
                const isExpanded = expandedId === product.shopifyId || expandedId === -1;
                return (
                  <div key={product.shopifyId} className="rounded-xl border border-gray-200 overflow-hidden bg-white hover:border-gray-300 transition-colors">
                    <button
                      onClick={() => setExpandedId(isExpanded && expandedId !== -1 ? null : product.shopifyId)}
                      className="w-full flex items-center gap-3 p-3 text-left"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        {product.image ? (
                          <img src={product.image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                            <ImageIcon className="h-4 w-4 text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-heading truncate">{product.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs font-medium text-heading">${parseFloat(product.price).toFixed(2)}</span>
                          <span className="text-[10px] text-primary bg-primary/5 px-1.5 py-0.5 rounded">{product.category}</span>
                          <span className="text-xs text-body">{product.variants.filter((v) => v.included).length} sizes</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] text-body bg-gray-100 px-2 py-1 rounded-lg">
                          <Edit3 className="h-3 w-3 inline mr-1" />Edit
                        </span>
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-100 p-4 space-y-4 bg-gray-50/30">
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-medium text-body uppercase tracking-wider">Product Name</label>
                          <input type="text" value={product.name} onChange={(e) => updateProduct(product.shopifyId, { name: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-medium text-body uppercase tracking-wider">Price</label>
                            <div className="relative">
                              <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                              <input type="number" step="0.01" min="0" value={product.price} onChange={(e) => updateProduct(product.shopifyId, { price: e.target.value })} className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white" />
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-medium text-body uppercase tracking-wider">Compare-at</label>
                            <div className="relative">
                              <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                              <input type="number" step="0.01" min="0" value={product.compareAtPrice} onChange={(e) => updateProduct(product.shopifyId, { compareAtPrice: e.target.value })} placeholder="—" className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white" />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-medium text-body uppercase tracking-wider">Category</label>
                          <select value={product.category} onChange={(e) => updateProduct(product.shopifyId, { category: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white appearance-none">
                            {PRODUCT_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-medium text-body uppercase tracking-wider">Tags</label>
                          <div className="relative">
                            <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input type="text" value={product.tags} onChange={(e) => updateProduct(product.shopifyId, { tags: e.target.value })} placeholder="summer, linen, casual" className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[11px] font-medium text-body uppercase tracking-wider">Sizes &amp; Stock</label>
                          <div className="space-y-1.5">
                            {product.variants.map((v, vi) => (
                              <div key={vi} className={cn('flex items-center gap-2 p-2 rounded-lg border transition-colors', v.included ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50')}>
                                <button onClick={() => updateVariant(product.shopifyId, vi, { included: !v.included })} className="flex-shrink-0">
                                  {v.included ? <ToggleRight className="h-5 w-5 text-primary" /> : <ToggleLeft className="h-5 w-5 text-gray-300" />}
                                </button>
                                <input type="text" value={v.label} onChange={(e) => updateVariant(product.shopifyId, vi, { label: e.target.value })} className={cn('w-20 px-2 py-1 border border-gray-200 rounded text-xs text-center focus:outline-none focus:border-primary', !v.included && 'opacity-40 bg-gray-50')} />
                                <div className="flex items-center gap-1 flex-1">
                                  <span className="text-[10px] text-body">Stock:</span>
                                  <input type="number" min="0" value={v.stock} onChange={(e) => updateVariant(product.shopifyId, vi, { stock: Math.max(0, parseInt(e.target.value) || 0) })} className={cn('w-16 px-2 py-1 border border-gray-200 rounded text-xs text-center focus:outline-none focus:border-primary', !v.included && 'opacity-40 bg-gray-50')} />
                                </div>
                                {v.stock === 0 && v.included && (
                                  <span className="text-[10px] text-red-500 bg-red-50 px-1.5 py-0.5 rounded flex-shrink-0">Out</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        <button onClick={() => updateProduct(product.shopifyId, { included: false })} className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1">
                          <X className="h-3.5 w-3.5" />Remove from import
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {includedCount === 0 && (
                <div className="text-center py-12">
                  <Package className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-body">No products selected</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => setStep('select')}>
                    <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Go back
                  </Button>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex items-center gap-3 flex-shrink-0 bg-white">
              <Button variant="outline" onClick={() => setStep('select')} className="flex-shrink-0">
                <ArrowLeft className="h-4 w-4 mr-1.5" />Back
              </Button>
              <Button onClick={handleImport} disabled={includedCount === 0} className="flex-1">
                <Sparkles className="h-4 w-4 mr-2" />
                Import {includedCount} Product{includedCount !== 1 ? 's' : ''}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
